import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import HomeIcon from '@mui/icons-material/Home';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const NotFound = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const bankBuildingVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8
      }
    }
  };

  const columnVariants = {
    hidden: { scaleY: 0 },
    visible: {
      scaleY: 1,
      transition: {
        duration: 0.4,
        delay: 0.3
      }
    }
  };

  const documentVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 20,
        delay: 0.6
      }
    },
    hover: {
      y: [-3, 3, -3, 3, 0],
      transition: {
        duration: 0.8
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 8
        }}
      >
        {/* Bank Building Animation */}
        <Box
          component={motion.div}
          variants={bankBuildingVariants}
          whileHover={{ scale: 1.05 }}
          sx={{
            width: '250px',
            height: '220px',
            position: 'relative',
            mb: 4
          }}
        >
          {/* Bank Building */}
          <Box
            sx={{
              width: '100%',
              height: '160px',
              bgcolor: '#E0E0E0',
              borderRadius: '10px',
              position: 'absolute',
              bottom: 0,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
          />

          {/* Roof/Top */}
          <Box
            sx={{
              width: '220px',
              height: '50px',
              bgcolor: '#1976D2',
              borderRadius: '8px 8px 0 0',
              position: 'absolute',
              bottom: '160px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />

          {/* Columns */}
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              component={motion.div}
              variants={columnVariants}
              custom={i * 0.1}
              sx={{
                width: '30px',
                height: '110px',
                bgcolor: '#FFFFFF',
                borderRadius: '5px',
                position: 'absolute',
                bottom: '25px',
                left: `${20 + i * 45}px`,
                transformOrigin: 'bottom',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
            />
          ))}

          {/* Steps */}
          <Box
            sx={{
              width: '180px',
              height: '20px',
              bgcolor: '#BDBDBD',
              borderRadius: '5px',
              position: 'absolute',
              bottom: '5px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />

          {/* Error Document */}
          <Box
            component={motion.div}
            variants={documentVariants}
            whileHover="hover"
            sx={{
              position: 'absolute',
              top: '30px',
              right: '-20px',
              width: '60px',
              height: '80px',
              bgcolor: '#F44336',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#fff',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '15px',
                width: '40px',
                height: '4px',
                bgcolor: '#fff'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '30px',
                width: '40px',
                height: '4px',
                bgcolor: '#fff'
              }
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '20px',
                mt: '10px'
              }}
            >
              404
            </Typography>
          </Box>
        </Box>

        {/* Text Content */}
        <Typography
          component={motion.h1}
          variants={itemVariants}
          sx={{
            fontSize: { xs: '4rem', md: '6rem' },
            fontWeight: 'bold',
            color: '#1A1A1A',
            mb: 2
          }}
        >
          404
        </Typography>

        <Typography
          component={motion.h2}
          variants={itemVariants}
          sx={{
            fontSize: { xs: '1.5rem', md: '2rem' },
            fontWeight: 'medium',
            color: '#666',
            mb: 3
          }}
        >
          Application Not Found
        </Typography>

        <Typography
          component={motion.p}
          variants={itemVariants}
          sx={{
            fontSize: '1.1rem',
            color: '#666',
            mb: 4,
            maxWidth: '600px'
          }}
        >
          Sorry, but we couldn't find the mortgage application or page you were looking for. Let's get you back to our main services.
        </Typography>

        <motion.div variants={itemVariants}>
          <Button
            variant="primary"
            onClick={() => navigate('/')}
            startIcon={<HomeIcon />}
          >
            Back to Home
          </Button>
        </motion.div>
      </Box>
    </Container>
  );
};

export default NotFound;